// What happens after a save's PUT, or a "Load the latest hours" (NO-878), as
// one injectable function so the order is testable without React: lock the
// editor, move the draft's baseline to what the save stored, read the hours
// back, adopt that read only when it succeeded, and always unlock. Relative
// imports only (`tsx --test`).

export interface SettleDeps<S> {
  /** Lock or unlock editing and saving. */
  lock: (locked: boolean) => void;
  /** Move the live draft's baseline to the saved hours, keeping the draft. */
  advance: (source: S) => void;
  /** Read the hours back; null when the read failed. */
  read: () => Promise<S | null>;
  /** Rebuild the draft from a successful read-back. */
  adopt: (source: S) => void;
  /** Whether a saved change could not be reloaded. */
  reloadFailed: (failed: boolean) => void;
}

export interface SettleInput<S> {
  /** The save went through (false for "Load the latest hours"). */
  saved: boolean;
  /** What the save stored, when its own answer says; null otherwise. */
  baseline: S | null;
}

async function readQuietly<S>(read: () => Promise<S | null>): Promise<S | null> {
  try {
    return await read();
  } catch {
    return null;
  }
}

export async function settleSave<S>(
  input: SettleInput<S>,
  deps: SettleDeps<S>
): Promise<void> {
  deps.lock(true);

  try {
    // The save is on the server whatever the read-back does, so the draft
    // stops counting as unsaved and the next Save uses the new version.
    if (input.saved && input.baseline !== null) deps.advance(input.baseline);

    const next = await readQuietly(deps.read);

    if (next !== null) deps.adopt(next);

    deps.reloadFailed(next === null && input.saved);
  } finally {
    deps.lock(false);
  }
}

/** The reload notice's Retry: replacing the draft with a fresh read asks first
 * when edits were made after the save (the guard only asks when dirty). */
export function guardedReload(
  guard: (action: () => void) => void,
  reload: () => Promise<void>
): () => void {
  return () => guard(() => void reload());
}
